import * as React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

const SCRIPT_ID = "google-recaptcha-v3-script";

type RecaptchaContextValue = {
	executeRecaptcha: ((action: string) => Promise<string>) | undefined;
};

const RecaptchaContext = createContext<RecaptchaContextValue>({
	executeRecaptcha: undefined,
});

export function RecaptchaProvider({
	siteKey,
	children,
}: {
	siteKey: string;
	children: React.ReactNode;
}) {
	const [ready, setReady] = useState(false);
	const siteKeyRef = useRef(siteKey);

	useEffect(() => {
		siteKeyRef.current = siteKey;
	}, [siteKey]);

	useEffect(() => {
		if (!siteKey) {
			setReady(false);
			return;
		}

		let cancelled = false;

		const whenLoaded = () => {
			const tick = () => {
				if (cancelled) return;
				if (window.grecaptcha?.ready) {
					window.grecaptcha.ready(() => {
						if (!cancelled) setReady(true);
					});
					return;
				}
				requestAnimationFrame(tick);
			};
			tick();
		};

		const existing = document.getElementById(SCRIPT_ID);
		if (existing) {
			whenLoaded();
			return () => {
				cancelled = true;
			};
		}

		const script = document.createElement("script");
		script.id = SCRIPT_ID;
		script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
			siteKey,
		)}`;
		script.async = true;
		script.onload = whenLoaded;
		document.head.appendChild(script);

		return () => {
			cancelled = true;
		};
	}, [siteKey]);

	const executeRecaptcha = useCallback(async (action: string) => {
		const key = siteKeyRef.current;
		if (!key || !window.grecaptcha?.execute) {
			throw new Error("reCAPTCHA not ready");
		}
		return window.grecaptcha.execute(key, { action });
	}, []);

	const value = useMemo(
		() => ({
			executeRecaptcha: ready && siteKey ? executeRecaptcha : undefined,
		}),
		[ready, siteKey, executeRecaptcha],
	);

	return (
		<RecaptchaContext.Provider value={value}>
			{children}
		</RecaptchaContext.Provider>
	);
}

export function useRecaptcha(): RecaptchaContextValue {
	return useContext(RecaptchaContext);
}
